import { lineClient, travelEsimClient } from '@/lib/api/ts-rest';
import { queryKeys } from '@/lib/query-keys';
import { useQuery } from '@tanstack/react-query';

/** Destinations of the international eSIM grid, for the catalog tab. */
export function useTravelEsimDestinations(organizationId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.catalog.travelEsimDestinations(organizationId),
    queryFn: async () => {
      const res = await travelEsimClient.listDestinations({
        params: { organizationId: organizationId! },
      });
      if (res.status !== 200) throw new Error('Failed to load destinations');
      return res.body;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The one offer of a destination for a stay (dates mandatory): richest data
 * tier, priced as the cheapest cover of the stay by the server.
 */
export function useTravelEsimQuote(
  organizationId: string | undefined,
  country: string | undefined,
  departureDate: string | undefined,
  returnDate: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.catalog.travelEsimQuote(
      organizationId,
      country,
      departureDate,
      returnDate,
    ),
    queryFn: async () => {
      const res = await travelEsimClient.getQuote({
        params: { organizationId: organizationId! },
        query: {
          country: country!,
          departureDate: departureDate!,
          returnDate: returnDate!,
        },
      });
      if (res.status === 404) return null;
      if (res.status !== 200) throw new Error('Failed to load the offer');
      return res.body;
    },
    enabled: !!organizationId && !!country && !!departureDate && !!returnDate,
    placeholderData: (previous) => previous,
  });
}

const LINE_SEARCH_LIMIT = 8;

/**
 * Fleet search of the order modal: name, e-mail or number (the server
 * normalises digits), over the whole organization whatever the default
 * billing centre. Off until the query has two characters.
 */
export function useTravelEsimLineSearch(
  organizationId: string | undefined,
  search: string,
) {
  const q = search.trim();
  return useQuery({
    queryKey: queryKeys.lines.list(undefined, organizationId, {
      page: 1,
      limit: LINE_SEARCH_LIMIT,
      searchText: q,
    }),
    queryFn: async () => {
      const res = await lineClient.listOrganizationLines({
        params: { organizationId: organizationId! },
        query: { page: 1, limit: LINE_SEARCH_LIMIT, q },
      });
      if (res.status !== 200) throw new Error('Failed to search lines');
      return res.body.lines;
    },
    enabled: !!organizationId && q.length >= 2,
    placeholderData: (previous) => previous,
    staleTime: 60 * 1000,
  });
}

/**
 * The eSIMs a beneficiary already holds (fleet line by id, outsider by
 * e-mail): the order dialog proposes a recharge when there is one.
 */
export function useBeneficiaryTravelEsims(
  organizationId: string | undefined,
  beneficiary: { lineId?: string; email?: string },
) {
  const { lineId, email } = beneficiary;
  return useQuery({
    queryKey: queryKeys.catalog.travelEsimLookup(organizationId, lineId, email),
    queryFn: async () => {
      const res = await travelEsimClient.lookupBeneficiaryTravelEsims({
        params: { organizationId: organizationId! },
        query: lineId ? { lineId } : { email: email! },
      });
      if (res.status !== 200) throw new Error('Failed to look up travel eSIMs');
      return res.body;
    },
    enabled: !!organizationId && (!!lineId || !!email),
  });
}
